import { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import { TIMELINE_CARD_VARIANTS } from './timelineCardClasses.js';
import TimelineCard from './TimelineCard.jsx';

/**
 * Timeline Widget (Figma DS AIMS OS — node 15990:8184).
 *
 * A read-only horizontal row of {@link TimelineCard}s connected by thin divider
 * lines. The dividers are visual separators — NOT process arrows; use a Step /
 * Progress component for mandatory sequences. Meant to live inside a card / panel
 * shell (which provides the title + actions); this renders the cards row only.
 *
 * Recommended 2–6 cards. Cards scale to fill the width; the row scrolls
 * horizontally if content exceeds the container.
 *
 * @param {object} props
 * @param {Array<{id?:string,variant?:string,value?:import('react').ReactNode,title?:import('react').ReactNode,icon?:import('lucide-react').LucideIcon}>} props.items
 */
const TimelineWidget = ({ items = [], className = '', ...props }) => {
  if (!items.length) return null;

  return (
    <div
      className={['flex w-full min-w-0 items-center overflow-x-auto', className].filter(Boolean).join(' ')}
      {...props}
    >
      {items.map((item, index) => (
        <Fragment key={item.id ?? index}>
          <TimelineCard
            variant={item.variant}
            value={item.value}
            title={item.title}
            icon={item.icon}
            className="min-w-[160px] flex-1"
          />
          {index < items.length - 1 ? (
            <span className="mx-2 h-px w-8 shrink-0 bg-edge" aria-hidden />
          ) : null}
        </Fragment>
      ))}
    </div>
  );
};

TimelineWidget.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      variant: PropTypes.oneOf(TIMELINE_CARD_VARIANTS),
      value: PropTypes.node,
      title: PropTypes.node,
      icon: PropTypes.elementType,
    }),
  ),
  className: PropTypes.string,
};

export default memo(TimelineWidget);
